import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { DevicesClass, DeviceTypes } from '@app/core/classes';
import { BookingService } from '@app/modules/booking-modules/booking.service';
import { BookingPerformerHiringStatus, PerformerHiringVm, resolveHiringConditions, EquipmentAvailability, AvailabilityOption } from '@app/modules/booking-modules/booking.model';
import { AccountService } from '@app/modules/account-modules/account.service';
import { getActiveBookingPerformerId } from '@app/modules/account-modules/account.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { AppStaticURL } from '@app/app.settings';
import { ConfirmationService } from 'primeng/api';

@UntilDestroy()
@Component({
  selector: 'app-account-invitations',
  templateUrl: './invitations.component.html',
  styleUrls: ['./invitations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvitationsComponent extends DevicesClass implements OnInit {
  readonly DTypes = DeviceTypes;
  readonly StaticURL = AppStaticURL;
  readonly pageSize = 20;
  readonly statusOptions = [
    { name: 'Все статусы', value: '' },
    { name: 'Ожидает', value: BookingPerformerHiringStatus.Pending },
    { name: 'Принята', value: BookingPerformerHiringStatus.Accepted },
    { name: 'Отклонена', value: BookingPerformerHiringStatus.Declined },
    { name: 'Завершена', value: BookingPerformerHiringStatus.Completed },
    { name: 'Отозвана', value: BookingPerformerHiringStatus.Withdrawn },
    { name: 'Неуспешна', value: BookingPerformerHiringStatus.Unsuccessful }
  ];

  performerId: string | null = null;
  loading = false;
  loadError: string | null = null;
  items: PerformerHiringVm[] = [];
  page = 1;
  hasMore = false;
  from = '';
  to = '';
  status: BookingPerformerHiringStatus | '' = '';
  actionLoadingId: string | null = null;

  constructor(
    injector: Injector,
    public cdr: ChangeDetectorRef,
    private bookingService: BookingService,
    private accountService: AccountService,
    private confirmationService: ConfirmationService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.resolvePerformerAndLoad();
  }

  onApplyFilters(): void {
    this.page = 1;
    this.loadInvitations();
  }

  onResetFilters(): void {
    this.from = '';
    this.to = '';
    this.status = '';
    this.page = 1;
    this.loadInvitations();
  }

  onPrevPage(): void {
    if (this.page <= 1 || this.loading) return;
    this.page -= 1;
    this.loadInvitations();
  }

  onNextPage(): void {
    if (!this.hasMore || this.loading) return;
    this.page += 1;
    this.loadInvitations();
  }

  get showPager(): boolean {
    return this.page > 1 || this.hasMore;
  }

  customerPhoto(item: PerformerHiringVm): string | null {
    const c = item?.customer;
    if (!c) return null;
    const rel = c.image?.thumbnailPath || c.image?.path || c.avatarPath || c.picPath || null;
    if (!rel) return null;
    return /^https?:\/\//i.test(rel) ? rel : (this.StaticURL + rel.replace(/^\//, ''));
  }

  customerName(item: PerformerHiringVm): string {
    return item?.customer?.name || 'Заказчик';
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  }

  statusLabel(status: BookingPerformerHiringStatus | null | undefined): string {
    const map: Record<string, string> = {
      [BookingPerformerHiringStatus.Pending]: 'Ожидает',
      [BookingPerformerHiringStatus.Accepted]: 'Принята',
      [BookingPerformerHiringStatus.Declined]: 'Отклонена',
      [BookingPerformerHiringStatus.Completed]: 'Исполнена',
      [BookingPerformerHiringStatus.Withdrawn]: 'Отозвана',
      [BookingPerformerHiringStatus.Unsuccessful]: 'Не состоялась'
    };
    return status && map[status] ? map[status] : '—';
  }

  statusClass(status: BookingPerformerHiringStatus | null | undefined): string {
    if (status === BookingPerformerHiringStatus.Pending) return 'pending';
    if (status === BookingPerformerHiringStatus.Accepted) return 'accepted';
    if (status === BookingPerformerHiringStatus.Declined) return 'declined';
    if (status === BookingPerformerHiringStatus.Completed) return 'completed';
    if (status === BookingPerformerHiringStatus.Withdrawn) return 'withdrawn';
    if (status === BookingPerformerHiringStatus.Unsuccessful) return 'unsuccessful';
    return 'unknown';
  }

  conditionsSummary(item: PerformerHiringVm): string {
    const c = resolveHiringConditions(item);
    if (!c) return '—';
    const parts: string[] = [];
    const amount = c?.honorarium?.amount;
    const isNegotiable = !!c?.honorarium?.isNegotiable;
    if (isNegotiable) {
      parts.push('Оплата: договорная');
    } else if (typeof amount === 'number' && amount > 0) {
      parts.push(`Оплата: ${new Intl.NumberFormat('ru-RU').format(amount)} ₽`);
    }
    const eq = c?.equipment?.availability;
    if (eq === EquipmentAvailability.Yes) parts.push('Аппаратура: есть');
    else if (eq === EquipmentAvailability.No) parts.push('Аппаратура: нет');
    else if (eq === EquipmentAvailability.Text) parts.push('Аппаратура: по договоренности');
    const acc = c?.coversAccommodation;
    if (acc === AvailabilityOption.Yes) parts.push('Проживание: нужно');
    else if (acc === AvailabilityOption.No) parts.push('Проживание: не нужно');
    else if (acc === AvailabilityOption.OnRequest) parts.push('Проживание: по запросу');
    return parts.length ? parts.join(', ') : '—';
  }

  canAcceptOrDecline(item: PerformerHiringVm): boolean {
    return item?.status === BookingPerformerHiringStatus.Pending;
  }

  actionDisabled(item: PerformerHiringVm): boolean {
    return this.loading || !item?.id || this.actionLoadingId === item.id;
  }

  confirmAccept(item: PerformerHiringVm): void {
    if (!this.canAcceptOrDecline(item) || this.actionLoadingId) return;
    this.confirmationService.confirm({
      key: 'invitations',
      header: 'Принять приглашение?',
      message: 'Статус приглашения изменится на «Принята».',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.runAccept(item)
    });
  }

  confirmDecline(item: PerformerHiringVm): void {
    if (!this.canAcceptOrDecline(item) || this.actionLoadingId) return;
    this.confirmationService.confirm({
      key: 'invitations',
      header: 'Отклонить приглашение?',
      message: 'Статус приглашения изменится на «Отклонена».',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.runDecline(item)
    });
  }

  private runAccept(item: PerformerHiringVm): void {
    const requestId = item?.id;
    if (!this.performerId || !requestId || !this.canAcceptOrDecline(item) || this.actionLoadingId) return;
    this.actionLoadingId = requestId;
    this.bookingService.acceptPerformerIncomingHiring(this.performerId, requestId)
      .pipe(
        finalize(() => {
          this.actionLoadingId = null;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe(
        () => this.loadInvitations(),
        err => {
          this.loadError = (err && (err.message || err.error?.message)) || 'Не удалось принять приглашение.';
          this.cdr.markForCheck();
        }
      );
  }

  private runDecline(item: PerformerHiringVm): void {
    const requestId = item?.id;
    if (!this.performerId || !requestId || !this.canAcceptOrDecline(item) || this.actionLoadingId) return;
    this.actionLoadingId = requestId;
    this.bookingService.declinePerformerIncomingHiring(this.performerId, requestId)
      .pipe(
        finalize(() => {
          this.actionLoadingId = null;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe(
        () => this.loadInvitations(),
        err => {
          this.loadError = (err && (err.message || err.error?.message)) || 'Не удалось отклонить приглашение.';
          this.cdr.markForCheck();
        }
      );
  }

  private resolvePerformerAndLoad(): void {
    this.loading = true;
    this.loadError = null;
    this.cdr.markForCheck();
    this.accountService
      .getMyInfo()
      .pipe(untilDestroyed(this))
      .subscribe(
        user => {
          this.accountService.onSetUserRole(user);
          this.performerId = getActiveBookingPerformerId(user);
          if (!this.performerId) {
            this.loading = false;
            this.loadError = 'Профиль исполнителя для букинга не найден.';
            this.cdr.markForCheck();
            return;
          }
          this.loadInvitations();
        },
        () => {
          this.loading = false;
          this.loadError = 'Не удалось загрузить данные аккаунта.';
          this.cdr.markForCheck();
        }
      );
  }

  private loadInvitations(): void {
    if (!this.performerId) return;
    this.loading = true;
    this.loadError = null;
    this.cdr.markForCheck();
    this.bookingService
      .getPerformerIncomingHirings(this.performerId, {
        page: this.page,
        size: this.pageSize,
        from: this.from || null,
        to: this.to || null,
        status: this.status || null
      })
      .pipe(untilDestroyed(this))
      .subscribe(
        list => {
          this.items = list || [];
          this.hasMore = (this.items?.length || 0) >= this.pageSize;
          this.loading = false;
          this.cdr.markForCheck();
        },
        err => {
          this.loading = false;
          this.items = [];
          this.hasMore = false;
          this.loadError = (err && (err.message || err.error?.message)) || 'Не удалось загрузить входящие приглашения.';
          this.cdr.markForCheck();
        }
      );
  }
}
